import { createBackend, Resources } from './backend';

const fetchPromiseCache = new Map<string, Promise<Resources | null>>();

const getCacheKey = (packageName: string, locale: string) => `${packageName}::${locale}`;

const getCachedFetchPromise = (packageName: string, locale: string) =>
  fetchPromiseCache.get(getCacheKey(packageName, locale));

const setCachedFetchPromise = (
  packageName: string,
  locale: string,
  fetchPromise: Promise<Resources | null>,
) => fetchPromiseCache.set(getCacheKey(packageName, locale), fetchPromise);

/**
 * Trigger a fetch of backend resources for a given package and locale. If a
 * previous fetch has already been started, the promise for that fetch will be
 * returned instead.
 * @param packageName The name of the package to fetch resources for.
 * @param locale The locale to fetch resources for.
 * @returns A promise that resolves once resources have been fetched.
 */
export const fetchBackendResources = (
  packageName: string,
  locale: string,
): Promise<Resources | null> => {
  const cachedFetchPromise = getCachedFetchPromise(packageName, locale);

  if (cachedFetchPromise) {
    return cachedFetchPromise;
  }

  const backend = createBackend(packageName, packageName);
  const fetchPromise = backend.fetch(locale);

  setCachedFetchPromise(packageName, locale, fetchPromise);

  return fetchPromise;
};
